import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import ListRows, { ListRowsOwnProps } from 'components/common/ListRows';
import RectangleRow from 'components/pages/home/RectangleRow';

import focusManager from 'support/focus/FocusManager';
import { VideoCtvRedux, RelatedItem } from 'store-types';
import CircleGrid, { CircleGridDataItem } from '../home/CircleGrid';
import styles from '../home/InteriorRows.module.scss';

interface LandingPageRowsProps {
  focusId?: string | number | null;
  renderBeforeFirst?: () => ReactNode;
  renderAfterLast?: () => ReactNode;
  onInteriorRowsFocusIndexChanged?: (index: number) => void;
  rowsRef?: React.Ref<React.Component<ListRowsOwnProps>> | null;
  pageName?: string | null;
  trackingRowOffset?: number | null;
}

const LandingPageRows = ({
  focusId: focusIdProp = null,
  renderBeforeFirst = () => null,
  renderAfterLast = () => null,
  onInteriorRowsFocusIndexChanged = () => {},
  rowsRef = null,
  pageName = null,
}: LandingPageRowsProps) => {
  const [focusId, setFocusId] = useState<string | number | null | undefined>(focusIdProp);

  useEffect(() => {
    if (focusIdProp) {
      setFocusId(focusIdProp);
    } else {
      setFocusId(focusManager.getNewUniqueId());
    }
  }, [focusIdProp]);
  const sportsData = useSelector((state: VideoCtvRedux) => state.kitchenSink.data.allsports);
  const showsData = useSelector((state: VideoCtvRedux) => state.kitchenSink.data.shows);

  let pageData: RelatedItem[] = [];
  if (pageName === 'all-leagues') {
    pageData = sportsData;
  } else if (pageName === 'all-shows') {
    pageData = showsData;
  }
  const rows = pageData;

  const onListRowsFocusIndexChanged = useCallback(
      (index: number) => {
      onInteriorRowsFocusIndexChanged?.(index);
    },
    [onInteriorRowsFocusIndexChanged],
  );
  const shouldSnapRowToTop = useCallback(() => true, []);
  const onEndOfNavigationOverride = (index: number) => {
    if (index === 0) {
      focusManager.setCurrentFocus('left-nav-focusable-content');
    }
  };

  const renderRow = useCallback(
    (row, rowIndex) => {
      if (!row.data?.length) return null;

      let focusIdPrefix: string | undefined;
      if (rowIndex === 0) {
        focusIdPrefix = 'firstrow-';
      } else if (rowIndex === rows.length - 1) {
        focusIdPrefix = 'lastrow-';
      }

      const getHomeRow = () => {
        return (
          <RectangleRow
            className={styles.row}
            focusIdPrefix={focusIdPrefix}
            displayTitle
            title={row.title}
            row={row}
            titleClassName={styles.rectangleRowTitle}
            rowClassName={classNames(styles.row, styles.inverseMargin)}
            rowPos={rowIndex + 1}
            onEndOfNavigationOverride={onEndOfNavigationOverride}
          />
        );
      };

      if (rowIndex === 0) {
        return (
          <div className={styles.firstRow}>
            <div className="flex-grow-1" />
            <div className={styles.beforeFirst}>{renderBeforeFirst?.()}</div>
            {getHomeRow()}
            {rows.length === 1 && renderAfterLast?.()}
          </div>
        );
      }

      if (rowIndex === rows.length - 1) {
        return (
          <>
            {getHomeRow()}
            {renderAfterLast?.()}
          </>
        );
      }

      return getHomeRow();
    },
    [renderAfterLast, renderBeforeFirst, rows.length],
  );

  if (pageName === 'all-shows') {
    return (
      <>
        <CircleGrid
          className={classNames(styles.padding)}
          focusId={focusId}
          numCols={6}
          data={(rows || []) as CircleGridDataItem[]}
          prevBuffer={0}
          nextBuffer={rows.length}
        />
      </>
    );
  }
  return (
    <>
      <ListRows
        ref={rowsRef}
        focusId={focusId}
        className={styles.listRows}
        data={rows || []}
        renderItem={renderRow}
        buffer={3}
        restorationId={`${focusId}`}
        onListRowsFocusIndexChanged={onListRowsFocusIndexChanged}
        shouldSnapRowToTop={shouldSnapRowToTop}
      />
    </>
  );
};

export default React.forwardRef<React.Component<ListRowsOwnProps>, LandingPageRowsProps>((props, ref) => (
  <LandingPageRows {...props} rowsRef={ref} />
));
